import {
  AFRIMED_MARKETS,
  DIASPORA_MARKETS,
  SADC_MARKETS,
  getMarketByDialCode,
} from "@/config/markets.js";

export const CROSS_BORDER_REFERENCE_CURRENCIES = ["USD", "GBP", "EUR", "ZAR"];
export const SPONSOR_DIASPORA_UPLIFT = 1;

const FALLBACK_USD_RATES = {
  AED: 3.67,
  AUD: 1.53,
  BWP: 13.6,
  CAD: 1.38,
  CHF: 0.83,
  EUR: 0.88,
  GBP: 0.75,
  KES: 129,
  NAD: 17.7,
  NGN: 1530,
  NZD: 1.66,
  RWF: 1440,
  USD: 1,
  ZAR: 17.7,
  ZMW: 27.1,
};

const FX_CACHE_PREFIX = "afrimed:sponsor-fx:v1";
const FRANKFURTER_ENDPOINT = "https://api.frankfurter.dev/v1/latest";

function todayCacheStamp() {
  return new Date().toISOString().slice(0, 10);
}

function readFxCache(key) {
  if (typeof window === "undefined") return null;
  try {
    const cached = JSON.parse(window.localStorage.getItem(key) || "null");
    if (!cached || typeof cached !== "object") return null;
    const rate = Number(cached.rate || 0);
    if (!Number.isFinite(rate) || rate <= 0) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeFxCache(keys = [], value = {}) {
  if (typeof window === "undefined") return;
  try {
    for (const key of keys) {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Best effort cache only.
  }
}

async function fetchFrankfurterRate(from, target) {
  const params = new URLSearchParams({
    base: from,
    symbols: target,
  });

  const response = await fetch(`${FRANKFURTER_ENDPOINT}?${params.toString()}`);
  if (!response.ok) throw new Error("Daily live FX unavailable");
  const payload = await response.json();
  const rate = Number(payload?.rates?.[target] || 0);
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error("Daily live FX unavailable");
  }

  return {
    rate,
    provider: "Frankfurter",
    timestamp: Date.parse(payload?.date || "") / 1000 || Date.now() / 1000,
  };
}

async function fetchLiveFxRate(from, target) {
  return fetchFrankfurterRate(from, target);
}

const MARKET_BASE_CURRENCY = {
  ae: "AED",
  ao: "AOA",
  au: "AUD",
  be: "EUR",
  bw: "BWP",
  ca: "CAD",
  cd: "CDF",
  ch: "CHF",
  cm: "XAF",
  de: "EUR",
  gb: "GBP",
  ke: "KES",
  ls: "LSL",
  mg: "MGA",
  mu: "MUR",
  mw: "MWK",
  mz: "MZN",
  na: "NAD",
  ng: "NGN",
  nz: "NZD",
  rw: "RWF",
  sc: "SCR",
  sz: "SZL",
  tz: "TZS",
  us: "USD",
  za: "ZAR",
  zm: "ZMW",
  zw: "USD",
};

const MARKET_PRESENTMENT_CURRENCY = {
  au: "AUD",
  be: "EUR",
  ca: "USD",
  ch: "CHF",
  de: "EUR",
  nz: "NZD",
  ae: "USD",
  gb: "GBP",
  us: "USD",
};

export function getMarketByName(name) {
  const normalized = String(name || "").trim().toLowerCase();
  return (
    AFRIMED_MARKETS.find(
      (market) => String(market?.name || "").trim().toLowerCase() === normalized
    ) || null
  );
}

export function resolveSponsorMarket({ sponsorCountry = "", sponsorDialCode = "" } = {}) {
  return (
    getMarketByName(sponsorCountry) ||
    getMarketByDialCode(sponsorDialCode) ||
    null
  );
}

export function resolveSponsorCurrency({
  sponsorCountry = "",
  sponsorDialCode = "",
  fallbackCurrency = "ZAR",
} = {}) {
  const market = resolveSponsorMarket({ sponsorCountry, sponsorDialCode });
  if (!market) return String(fallbackCurrency || "ZAR").trim().toUpperCase();
  return MARKET_PRESENTMENT_CURRENCY[market.iso] || String(fallbackCurrency || "ZAR").trim().toUpperCase();
}

export function normalizeCurrencyCode(value = "", fallbackCurrency = "ZAR") {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized) return normalized;
  return String(fallbackCurrency || "ZAR").trim().toUpperCase() || "ZAR";
}

export function resolveCountryCurrency({
  country = "",
  fallbackCurrency = "ZAR",
} = {}) {
  const market = getMarketByName(country);
  if (!market) return normalizeCurrencyCode("", fallbackCurrency);
  return MARKET_BASE_CURRENCY[market.iso] || normalizeCurrencyCode("", fallbackCurrency);
}

export function resolvePaymentCurrency({
  explicitCurrency = "",
  sponsorCountry = "",
  sponsorDialCode = "",
  country = "",
  fallbackCurrency = "ZAR",
} = {}) {
  const directCurrency = normalizeCurrencyCode(explicitCurrency, "");
  if (directCurrency) return directCurrency;

  if (String(sponsorCountry || "").trim() || String(sponsorDialCode || "").trim()) {
    return resolveSponsorCurrency({
      sponsorCountry,
      sponsorDialCode,
      fallbackCurrency,
    });
  }

  if (String(country || "").trim()) {
    return resolveCountryCurrency({ country, fallbackCurrency });
  }

  return normalizeCurrencyCode("", fallbackCurrency);
}

export async function quoteSponsorPresentment({
  amount = "",
  sourceCurrency = "ZAR",
  sponsorCountry = "",
  sponsorDialCode = "",
} = {}) {
  const numericAmount = Number(String(amount || "").replace(/,/g, "."));
  const from = String(sourceCurrency || "ZAR").trim().toUpperCase();
  const target = resolveSponsorCurrency({
    sponsorCountry,
    sponsorDialCode,
    fallbackCurrency: from,
  });
  const upliftMultiplier = SPONSOR_DIASPORA_UPLIFT;

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return {
      amount: "",
      currency: target,
      upliftMultiplier,
      reference: "ECB daily reference",
    };
  }

  if (from === target) {
    return {
      amount: (numericAmount * upliftMultiplier).toFixed(2),
      currency: target,
      rate: 1,
      upliftMultiplier,
      reference: "No FX conversion needed",
    };
  }

  const pairKey = `${FX_CACHE_PREFIX}:${from}:${target}`;
  const dayKey = `${pairKey}:${todayCacheStamp()}`;
  const cachedToday = readFxCache(dayKey);
  if (cachedToday) {
    return {
      amount: (numericAmount * Number(cachedToday.rate) * upliftMultiplier).toFixed(2),
      currency: target,
      rate: Number(cachedToday.rate),
      upliftMultiplier,
      reference: "Cached FX",
    };
  }

  try {
    const liveQuote = await fetchLiveFxRate(from, target);
    writeFxCache([dayKey, `${pairKey}:latest`], {
      rate: liveQuote.rate,
      provider: liveQuote.provider,
      timestamp: liveQuote.timestamp,
      cachedAt: new Date().toISOString(),
    });

    return {
      amount: (numericAmount * liveQuote.rate * upliftMultiplier).toFixed(2),
      currency: target,
      rate: liveQuote.rate,
      upliftMultiplier,
      reference: "Live FX",
    };
  } catch {
    const latestCached = readFxCache(`${pairKey}:latest`);
    if (latestCached) {
      return {
        amount: (numericAmount * Number(latestCached.rate) * upliftMultiplier).toFixed(2),
        currency: target,
        rate: Number(latestCached.rate),
        upliftMultiplier,
        reference: "Cached FX",
      };
    }

    const sourceUsdRate = Number(FALLBACK_USD_RATES[from] || 0);
    const targetUsdRate = Number(FALLBACK_USD_RATES[target] || 0);
    const fallbackRate =
      sourceUsdRate > 0 && targetUsdRate > 0 ? targetUsdRate / sourceUsdRate : 0;

    if (fallbackRate > 0 && Number.isFinite(fallbackRate)) {
      return {
        amount: (numericAmount * fallbackRate * upliftMultiplier).toFixed(2),
        currency: target,
        rate: fallbackRate,
        upliftMultiplier,
        reference: "AfriMed fallback FX estimate",
      };
    }

    return {
      amount: (numericAmount * upliftMultiplier).toFixed(2),
      currency: from,
      rate: 1,
      upliftMultiplier,
      reference: "FX unavailable; source currency retained",
    };
  }
}

export function getMarketPaymentCoverage() {
  return {
    sadcCount: SADC_MARKETS.length,
    diasporaCount: DIASPORA_MARKETS.length,
    totalCount: AFRIMED_MARKETS.length,
    referenceCurrencies: CROSS_BORDER_REFERENCE_CURRENCIES,
  };
}

function normalizeAmountLabel(amount, currency) {
  const rawAmount = String(amount || "").trim();
  const code = String(currency || "").trim().toUpperCase();
  if (!rawAmount && !code) return "";
  if (!rawAmount) return code;
  if (!code) return rawAmount;
  return `${rawAmount} ${code}`;
}

export function buildSponsorPaymentRequestMessage({
  sponsorName = "",
  relationship = "",
  sponsorCountry = "",
  sponsorDialCode = "",
  sponsorPhone = "",
  sponsorEmail = "",
  paymentFor = "",
  amount = "",
  currency = "",
  paymentLinkUrl = "",
}) {
  const phoneLabel = [String(sponsorDialCode || "").trim(), String(sponsorPhone || "").trim()]
    .filter(Boolean)
    .join(" ");
  const amountLabel = normalizeAmountLabel(amount, currency);
  const linkLabel = String(paymentLinkUrl || "").trim();

  return [
    "AfriMed™ sponsor payment request",
    "",
    sponsorName ? `Sponsor: ${sponsorName}` : "",
    relationship ? `Relationship: ${relationship}` : "",
    sponsorCountry ? `Sponsor country: ${sponsorCountry}` : "",
    phoneLabel ? `Sponsor phone: ${phoneLabel}` : "",
    sponsorEmail ? `Sponsor email: ${sponsorEmail}` : "",
    paymentFor ? `Payment for: ${paymentFor}` : "",
    amountLabel ? `Requested amount: ${amountLabel}` : "",
    linkLabel ? `Secure payment link: ${linkLabel}` : "",
    "",
    linkLabel
      ? "This secure AfriMed payment link was prepared for sponsor payment."
      : "Generate the secure AfriMed payment link before sending this request so the sponsor receives a real payment action.",
    "Cross-border payments should respect fair FX presentment so the consultation is funded properly.",
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildSponsorPaymentMailto({
  sponsorEmail = "",
  subject = "",
  message = "",
}) {
  const email = String(sponsorEmail || "").trim();
  if (!email) return "";

  const encodedSubject = encodeURIComponent(
    String(subject || "AfriMed™ payment request").trim()
  );
  const body = encodeURIComponent(String(message || "").trim());
  return `mailto:${email}?subject=${encodedSubject}&body=${body}`;
}

export function getDialCodeMarketLabel(dialCode) {
  const market = getMarketByDialCode(dialCode);
  return market?.name || "";
}

export default {
  buildSponsorPaymentMailto,
  buildSponsorPaymentRequestMessage,
  CROSS_BORDER_REFERENCE_CURRENCIES,
  getDialCodeMarketLabel,
  getMarketByName,
  getMarketPaymentCoverage,
  normalizeCurrencyCode,
  resolveCountryCurrency,
  resolvePaymentCurrency,
};
